/**
 * Appels réseau de la campagne de réabonnement — CAM-01 à CAM-06.
 *
 * Ce fichier ne contient QUE des fonctions de transport : un appel = une route
 * du contrat (ARCHITECTURE §8). Les hooks TanStack Query vivent dans
 * `hooks.ts`, aucun composant n’appelle `api.*` directement.
 *
 * Routes du module `campagne` :
 *   - `POST /campagne/:saison/correspondances`  — aperçu chiffré, ne modifie rien
 *   - `POST /campagne/:saison/generer`          — génération (`simulation` possible)
 *   - `GET  /campagne/:saison/propositions`
 *   - `POST /propositions/:id/accepter` · `/decliner`
 *   - `PUT  /propositions/:id/sieges`
 *   - `GET  /campagne/:saison/suivi`
 *   - `POST /saisons/:id/phase`                 — transitions (CAM-02)
 *
 * Routes empruntées à d’autres modules, en lecture seule :
 *   - `GET /saisons`                    (catalogue) — choisir la saison N et N-1
 *   - `GET /formules?saisonId=`         (formules)  — série de la formule N,
 *     nécessaire pour ouvrir le plan de salle au moment de replacer des sièges
 *   - `GET /representations/:id/plan`   (ventes)    — l’endpoint pivot du plan
 */

import type {
  ApercuCorrespondances,
  CorrespondanceFormule,
  EtatPretAVendre,
  FormuleDto,
  PagePropositions,
  PhaseSaison,
  PlanRepresentation,
  PropositionDto,
  ResultatAcceptation,
  ResultatChangementPhase,
  ResultatGeneration,
  Saison,
  StatutProposition,
  SuiviCampagne,
} from '@tb/shared'

import { api } from '../../lib/api'

/* -------------------------------------------------------------------------- */
/* Clés de cache                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Toutes les clés partent de `['campagne']` : une mutation invalide la racine
 * et l’écran entier se remet à jour (compteurs, liste, suivi) sans que chaque
 * appelant ait à énumérer ce qu’il a changé.
 */
export const clesCampagne = {
  racine: ['campagne'] as const,
  saisons: ['campagne', 'saisons'] as const,
  suivi: (saisonId: string) => ['campagne', saisonId, 'suivi'] as const,
  pret: (saisonId: string) => ['campagne', saisonId, 'pret'] as const,
  correspondances: (saisonId: string, saisonN1Id: string, signature: string) =>
    ['campagne', saisonId, 'correspondances', saisonN1Id, signature] as const,
  propositions: (saisonId: string, filtres: unknown) =>
    ['campagne', saisonId, 'propositions', filtres] as const,
  formules: (saisonId: string) => ['campagne', 'formules', saisonId] as const,
  plan: (representationId: string) => ['campagne', 'plan', representationId] as const,
} as const

/* -------------------------------------------------------------------------- */
/* Catalogue et formules (lecture)                                            */
/* -------------------------------------------------------------------------- */

export function listerSaisons(signal?: AbortSignal): Promise<Saison[]> {
  return api.get<Saison[]>('/saisons', signal === undefined ? {} : { signal })
}

export function listerFormules(saisonId: string, signal?: AbortSignal): Promise<FormuleDto[]> {
  return api.get<FormuleDto[]>('/formules', {
    query: { saisonId },
    ...(signal === undefined ? {} : { signal }),
  })
}

/** Endpoint pivot : sièges, états, zones — c’est lui qui alimente le SeatMap. */
export function obtenirPlan(
  representationId: string,
  signal?: AbortSignal,
): Promise<PlanRepresentation> {
  return api.get<PlanRepresentation>(
    `/representations/${representationId}/plan`,
    signal === undefined ? {} : { signal },
  )
}

/* -------------------------------------------------------------------------- */
/* CAM-03 — correspondance des formules puis génération                       */
/* -------------------------------------------------------------------------- */

export interface EntreeCorrespondances {
  readonly saisonId: string
  readonly saisonN1Id: string
  readonly correspondances: readonly CorrespondanceFormule[]
}

/**
 * Aperçu de la table de correspondance. C’est un POST **sans effet de bord** :
 * le corps porte les choix humains déjà faits, la réponse chiffre ce que la
 * génération produirait.
 */
export function apercuCorrespondances(
  entree: EntreeCorrespondances,
  signal?: AbortSignal,
): Promise<ApercuCorrespondances> {
  return api.post<ApercuCorrespondances>(
    `/campagne/${entree.saisonId}/correspondances`,
    { saisonN1Id: entree.saisonN1Id, correspondances: entree.correspondances },
    signal === undefined ? {} : { signal },
  )
}

export interface EntreeGeneration extends EntreeCorrespondances {
  /** `true` : on chiffre sans rien écrire (alimente le `<ConfirmDialog>`). */
  readonly simulation: boolean
}

export function genererPropositions(entree: EntreeGeneration): Promise<ResultatGeneration> {
  return api.post<ResultatGeneration>(`/campagne/${entree.saisonId}/generer`, {
    saisonN1Id: entree.saisonN1Id,
    correspondances: entree.correspondances,
    simulation: entree.simulation,
  })
}

/* -------------------------------------------------------------------------- */
/* Propositions                                                               */
/* -------------------------------------------------------------------------- */

export interface FiltresPropositions {
  readonly page: number
  readonly taille: number
  readonly statut?: StatutProposition | undefined
  readonly aReplacer?: boolean | undefined
  readonly q?: string | undefined
}

export function listerPropositions(
  saisonId: string,
  filtres: FiltresPropositions,
  signal?: AbortSignal,
): Promise<PagePropositions> {
  return api.get<PagePropositions>(`/campagne/${saisonId}/propositions`, {
    query: {
      page: filtres.page,
      taille: filtres.taille,
      statut: filtres.statut ?? null,
      aReplacer: filtres.aReplacer === undefined ? null : String(filtres.aReplacer),
      q: filtres.q === undefined || filtres.q === '' ? null : filtres.q,
    },
    ...(signal === undefined ? {} : { signal }),
  })
}

/**
 * ★ « Prêt à vendre ? » — la complétude sans rien engager.
 *
 * Même règle que le refus d'ouverture d'une phase, en lecture. Voir
 * `etatPretAVendreSchema` : la question ne se posait qu'en TENTANT d'ouvrir.
 */
export function obtenirPretAVendre(
  saisonId: string,
  signal?: AbortSignal,
): Promise<EtatPretAVendre> {
  return api.get<EtatPretAVendre>(
    `/saisons/${saisonId}/pret`,
    signal === undefined ? {} : { signal },
  )
}

export function obtenirSuivi(saisonId: string, signal?: AbortSignal): Promise<SuiviCampagne> {
  return api.get<SuiviCampagne>(
    `/campagne/${saisonId}/suivi`,
    signal === undefined ? {} : { signal },
  )
}

/* -------------------------------------------------------------------------- */
/* CAM-05 — traitement des bulletins                                          */
/* -------------------------------------------------------------------------- */

export interface EntreeAcceptation {
  readonly propositionId: string
  /** Remplace les sièges au moment d’accepter (« ils avancent d’un rang »). */
  readonly siegeIds?: readonly string[] | undefined
  readonly note?: string | undefined
}

export function accepterProposition(entree: EntreeAcceptation): Promise<ResultatAcceptation> {
  const corps: Record<string, unknown> = {}
  if (entree.siegeIds !== undefined) corps.siegeIds = entree.siegeIds
  if (entree.note !== undefined && entree.note !== '') corps.note = entree.note
  return api.post<ResultatAcceptation>(`/propositions/${entree.propositionId}/accepter`, corps)
}

/** Décliner libère les sièges immédiatement. Motif facultatif (CAM-05). */
export function declinerProposition(
  propositionId: string,
  motif?: string,
): Promise<PropositionDto> {
  const corps: Record<string, unknown> = {}
  if (motif !== undefined && motif !== '') corps.motif = motif
  return api.post<PropositionDto>(`/propositions/${propositionId}/decliner`, corps)
}

/** Remplace intégralement les sièges gelés par la proposition. */
export function deplacerSieges(
  propositionId: string,
  siegeIds: readonly string[],
): Promise<PropositionDto> {
  return api.put<PropositionDto>(`/propositions/${propositionId}/sieges`, { siegeIds })
}

/* -------------------------------------------------------------------------- */
/* CAM-02 — transitions de phase                                              */
/* -------------------------------------------------------------------------- */

export interface EntreeChangementPhase {
  readonly saisonId: string
  readonly phase: PhaseSaison
  readonly simulation: boolean
  /**
   * Pourquoi maintenant — **exigé** par l’API pour une clôture réelle
   * (`changerPhaseSaisonSchema`), qui le journalise dans l’audit de la saison.
   * La simulation en est dispensée : c’est elle qui alimente le dialogue où
   * l’utilisateur saisit justement ce motif.
   */
  readonly motif?: string | undefined
}

export function changerPhase(entree: EntreeChangementPhase): Promise<ResultatChangementPhase> {
  return api.post<ResultatChangementPhase>(`/saisons/${entree.saisonId}/phase`, {
    phase: entree.phase,
    simulation: entree.simulation,
    ...(entree.motif === undefined ? {} : { motif: entree.motif }),
  })
}
